import type { TextSize, DictationRecommendation } from "@lingoshade/domain";

export interface DictationSettings {
  preferredVoiceId?: string;
  pauseBetweenWords: number;
  pauseBetweenSentences: number;
  playbackSpeed: number;
  autoRepeat: boolean;
  timeoutPerSentence: number;
}

export interface DictationSessionRequest {
  textSize?: TextSize;
  textId?: string;
  language?: string;
  excludeTextIds?: string[];
}

export interface DictationSessionResponse {
  id: string;
  textId: string;
  content: string;
  language: string;
  level: string;
  voiceId: string;
  sentences: string[];
  settings: DictationSettings;
}

export interface DictationSubmissionRequest {
  sessionId: string;
  userInput: string;
  userInputsBySentence?: string[]; // Новое поле для умного алгоритма
  timeSpentMs: number;
  sentenceRepeats: { [sentenceIndex: number]: number };
  wordBoundaries: Array<{
    word: string;
    offset: number;
    duration: number;
  }>;
}

export interface DictationResult {
  sessionId: string;
  baseAccuracy: number;
  finalAccuracy: number;
  totalRepeats: number;
  textMarkup: Array<{
    text: string;
    isError: boolean;
    expectedWord?: string;
    actualWord?: string;
    mistakeType?: string;
    wordIndex?: number;
  }>;
  smartComparison: SmartTextComparisonResult; // Новый формат вместо legacy
  timeSpentMs: number;
  wordsPerMinute?: number;
}

// Умное сравнение текстов - новые типы
export interface SmartWordAlignment {
  expectedWord: string | null;
  actualWord: string | null;
  isCorrect: boolean;
  similarity: number;
  type: "correct" | "incorrect" | "missing" | "extra";
}

export interface SmartSentenceComparison {
  sentenceIndex: number;
  expectedSentence: string;
  actualSentence: string;
  alignments: SmartWordAlignment[];
  accuracy: number;
  correctWords: number;
  totalExpectedWords: number;
  mistakes: {
    missing: string[];
    incorrect: Array<{ expected: string; actual: string; similarity: number }>;
    extra: string[];
  };
}

export interface SmartTextComparisonResult {
  sentences: SmartSentenceComparison[];
  overallAccuracy: number;
  totalCorrectWords: number;
  totalExpectedWords: number;
  summary: {
    perfectSentences: number;
    totalSentences: number;
    averageAccuracy: number;
  };
}

export interface DictationMistakeResult {
  sentenceIndex: number;
  wordIndex: number;
  expectedWord: string;
  actualWord: string;
  mistakeType: "missing" | "extra" | "wrong" | "typo";
}

export interface DictationStats {
  totalSessions: number;
  completedSessions: number;
  averageAccuracy: number;
  bestAccuracy: number;
  totalTimeSpent: number;
  averageWordsPerMinute?: number;
  totalRepeatsUsed: number;
  perfectSessions: number;
}

export interface DictationHistoryItem {
  id: string;
  textTitle: string;
  language: string;
  level: string;
  completedAt: string;
  finalAccuracy: number;
  totalRepeats: number;
  timeSpentMs: number;
}

// NOTE: Нельзя использовать enum из-за ограничения "erasableSyntaxOnly"
// Поэтому реализуем безопасный объект со строковыми литералами + тип.
// Это даёт те же преимущества автодополнения и сужения типов.
export const DictationStatus = Object.freeze({
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  PLAYING: "playing",
  PAUSED: "paused",
  WAITING_INPUT: "waiting_input",
  COMPLETED: "completed",
  ERROR: "error",
});
// eslint-disable-next-line no-redeclare -- Intentional value/type dual declaration for enum-like developer ergonomics
export type DictationStatus =
  (typeof DictationStatus)[keyof typeof DictationStatus];

// Helper type guard (если придёт внешнее значение)
export function isDictationStatus(value: unknown): value is DictationStatus {
  return (
    typeof value === "string" &&
    (Object.values(DictationStatus) as string[]).includes(value)
  );
}

export interface DictationState {
  recommendationTextSize: TextSize | "ALL";
  recommendation: DictationRecommendation | null;
  recommendationLoading: boolean;
  recommendationError: string | null;
  recommendationRequestId: string | null;
  status: DictationStatus;
  session: DictationSessionResponse | null;
  currentSentenceIndex: number;
  userInput: string;
  userInputs: string[];
  sentenceRepeats: { [index: number]: number };
  startTime: number | null;
  result: DictationResult | null;
  error: string | null;
  settings: DictationSettings;
  stats: DictationStats | null;
  history: DictationHistoryItem[];
  // Audio state
  volume: number;
  currentTime: number;
  audioDuration: number;
  audioLoaded: boolean;
  hasStarted: boolean; // Флаг, началась ли сессия (было ли первое воспроизведение)
  playedSentences: { [index: number]: boolean }; // Трекер воспроизведенных предложений
  isPlayingAudio: boolean; // Флаг для предотвращения двойного воспроизведения
}
